import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Card from '../../components/base/Card';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import API_BASE_URL from '../../config/api';
import { isSuperAdmin } from '../../utils/roleHelpers';

// Helper function to generate pagination page numbers with ellipsis
const getPaginationPages = (currentPage: number, totalPages: number, maxVisible: number = 7): (number | string)[] => {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | string)[] = [];
  const halfVisible = Math.floor(maxVisible / 2);

  if (currentPage <= halfVisible + 1) {
    for (let i = 1; i <= maxVisible - 2; i++) {
      pages.push(i);
    }
    pages.push('ellipsis');
    pages.push(totalPages);
  } else if (currentPage >= totalPages - halfVisible) {
    pages.push(1);
    pages.push('ellipsis');
    for (let i = totalPages - (maxVisible - 3); i <= totalPages; i++) {
      pages.push(i);
    }
  } else {
    pages.push(1);
    pages.push('ellipsis');
    for (let i = currentPage - 1; i <= currentPage + 1; i++) {
      pages.push(i);
    }
    pages.push('ellipsis');
    pages.push(totalPages);
  }

  return pages;
};

interface Company {
  company_id: string;
  description: string;
  created_at: string;
}

export default function AdminMaster() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [newCompany, setNewCompany] = useState({
    company_id: '',
    description: '',
  });
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCompanies, setTotalCompanies] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const companiesPerPage = 10;

  useEffect(() => {
    // Only super_admin can access this page
    if (!isSuperAdmin()) {
      navigate('/home');
      return;
    }

    // Diagnostic: Test if backend is accessible
    const testBackendConnection = async () => {
      try {
        // Test a known working endpoint
        const userResp = await fetch(`${API_BASE_URL}/user`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
          },
        });
        console.log('[Diagnostic] /user endpoint status:', userResp.status);
        
        // Test other admin endpoints to see which ones work
        const adminUsersResp = await fetch(`${API_BASE_URL}/admin/users`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
          },
        });
        console.log('[Diagnostic] /admin/users endpoint status:', adminUsersResp.status);
        
        // Test the companies endpoint
        const companiesResp = await fetch(`${API_BASE_URL}/admin/companies`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
          },
        });
        console.log('[Diagnostic] /admin/companies endpoint status:', companiesResp.status);
        console.log('[Diagnostic] /admin/companies response headers:', [...companiesResp.headers.entries()]);
        
        if (companiesResp.status === 404) {
          const text = await companiesResp.text();
          console.warn('[Diagnostic] 404 response body (first 200 chars):', text.substring(0, 200));
        }
      } catch (e) {
        console.error('[Diagnostic] Error testing endpoints:', e);
      }
    };
    
    testBackendConnection();
    loadCompanies(1);
  }, [navigate]);

  const loadCompanies = async (page: number = currentPage) => {
    setIsLoading(true);
    setError('');
    try {
      const apiUrl = `${API_BASE_URL}/admin/companies?page=${page}&page_size=${companiesPerPage}`;
      console.log('Fetching companies from:', apiUrl);
      
      const resp = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });
      
      // Check if response is ok before parsing JSON
      if (!resp.ok) {
        console.error(`HTTP error! status: ${resp.status}, URL: ${apiUrl}`);
        let errorMessage = '';
        
        // Check content type before trying to parse
        const contentType = resp.headers.get('content-type');
        const isJson = contentType && contentType.includes('application/json');
        
        if (resp.status === 404) {
          errorMessage = `Endpoint not found (404). The backend route /admin/companies may not be deployed or accessible at ${apiUrl}. Please verify:\n1. Backend server is running\n2. The route is correctly deployed\n3. API_BASE_URL is correct (currently: ${API_BASE_URL})`;
        } else if (isJson) {
        try {
          const errorData = await resp.json();
            errorMessage = errorData.error || `Failed to load companies (${resp.status})`;
          } catch (parseError) {
            errorMessage = `Failed to load companies: ${resp.status} ${resp.statusText}`;
          }
        } else {
          // Response is not JSON (likely HTML error page)
          const textResponse = await resp.text();
          console.error('Non-JSON error response:', textResponse.substring(0, 200));
          errorMessage = `Server error (${resp.status}): ${resp.statusText}. The endpoint may not exist or the server returned an HTML error page.`;
        }
        
        setError(errorMessage);
        setCompanies([]);
        return;
      }
      
      // Verify response is JSON before parsing
      const contentType = resp.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await resp.text();
        console.error('Expected JSON but got:', contentType, textResponse.substring(0, 200));
        setError(`Server returned non-JSON response. Expected JSON but got: ${contentType}`);
        setCompanies([]);
        return;
      }
      
      const data = await resp.json();
      if (data.success && Array.isArray(data.companies)) {
        setCompanies(data.companies);
        setTotalCompanies(data.total || 0);
        setTotalPages(data.total_pages || 0);
        setCurrentPage(data.page || page);
      } else {
        console.error('Failed to load companies:', data.error || data);
        setError(data.error || 'Failed to load companies');
        setCompanies([]);
        setTotalCompanies(0);
        setTotalPages(0);
      }
    } catch (e) {
      console.error('Failed to load companies', e);
      let errorMessage = '';
      
      if (e instanceof SyntaxError && e.message.includes('JSON')) {
        errorMessage = `JSON parse error: Server returned invalid JSON. This usually means the endpoint doesn't exist (404) or the server returned an HTML error page. Check if the backend route /admin/companies is deployed correctly.`;
      } else if (e instanceof TypeError && e.message.includes('fetch')) {
        errorMessage = `Network error: Unable to connect to API server at ${API_BASE_URL}. Please check:\n1. VITE_API_URL environment variable is set correctly\n2. Backend server is running and accessible\n3. CORS is configured properly`;
      } else {
        errorMessage = `Failed to load companies: ${e instanceof Error ? e.message : 'Unknown error'}`;
      }
      
      setError(errorMessage);
      setCompanies([]);
      setTotalCompanies(0);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCompany = async () => {
    if (!newCompany.company_id.trim()) {
      setError('Company ID is required');
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccessMessage('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/admin/companies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({
          company_id: newCompany.company_id.trim(),
          description: newCompany.description.trim() || undefined,
        }),
      });

      // Check if response is ok before parsing JSON
      if (!response.ok) {
        console.error(`HTTP error! status: ${response.status}`);
        try {
          const errorData = await response.json();
          setError(errorData.error || `Failed to create company (${response.status})`);
        } catch {
          setError(`Failed to create company: ${response.status} ${response.statusText}`);
        }
        return;
      }

      const result = await response.json();
      if (result.success) {
        setSuccessMessage('Company created successfully!');
        setNewCompany({ company_id: '', description: '' });
        // Reload companies list
        await loadCompanies();
        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(result.error || 'Failed to create company');
      }
    } catch (error) {
      console.error('Network error:', error);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteCompany = async (companyId: string) => {
    if (!window.confirm(`Are you sure you want to delete company ID "${companyId}"?`)) {
      return;
    }

    setIsDeleting(companyId);
    setError('');
    setSuccessMessage('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/admin/companies/${encodeURIComponent(companyId)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });

      // Check if response is ok before parsing JSON
      if (!response.ok) {
        console.error(`HTTP error! status: ${response.status}`);
        try {
          const errorData = await response.json();
          setError(errorData.error || `Failed to delete company (${response.status})`);
        } catch {
          setError(`Failed to delete company: ${response.status} ${response.statusText}`);
        }
        return;
      }

      const result = await response.json();
      if (result.success) {
        setSuccessMessage('Company deleted successfully!');
        // Reload companies list
        await loadCompanies();
        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(result.error || 'Failed to delete company');
      }
    } catch (error) {
      console.error('Network error:', error);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsDeleting(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50">
      <Navbar showUserActions={true} />
      
      <div className="flex flex-col items-center px-4 py-8">
        <div className="w-full max-w-7xl">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-800 mb-2">Admin Master</h1>
              <div className="w-full h-px bg-gray-300"></div>
            </div>
            <Button variant="outline" onClick={() => navigate('/home')}>
              <i className="ri-arrow-left-line mr-2"></i>
              Back to Home
            </Button>
          </div>

          {/* Success/Error Messages */}
          {successMessage && (
            <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
              <div className="flex items-center gap-2">
                <i className="ri-check-line"></i>
                {successMessage}
              </div>
            </div>
          )}
          {error && (
            <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              <div className="flex items-center gap-2">
                <i className="ri-error-warning-line"></i>
                {error}
              </div>
            </div>
          )}

          {/* Create Company Form */}
          <Card className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">Create New Company ID</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                type="text"
                placeholder="Company ID"
                value={newCompany.company_id}
                onChange={(e) => {
                  setNewCompany({ ...newCompany, company_id: e.target.value });
                  setError('');
                }}
                label="Company ID *"
              />
              <Input
                type="text"
                placeholder="Description (optional)"
                value={newCompany.description}
                onChange={(e) => {
                  setNewCompany({ ...newCompany, description: e.target.value });
                  setError('');
                }}
                label="Description"
              />
            </div>
            <div className="flex justify-end mt-6">
              <Button 
                onClick={handleCreateCompany} 
                disabled={isSaving || !newCompany.company_id.trim()}
                className="whitespace-nowrap"
              >
                {isSaving ? (
                  <>
                    <i className="ri-loader-line animate-spin mr-2"></i>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="ri-save-line mr-2"></i>
                    Save
                  </>
                )}
              </Button>
            </div>
          </Card>

          {/* Companies List */}
          <Card>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold text-gray-800">Existing Company IDs</h2>
              <Button
                variant="outline"
                onClick={() => loadCompanies(1)}
                disabled={isLoading}
                className="flex items-center"
              >
                <i className={`ri-refresh-line mr-2 ${isLoading ? 'animate-spin' : ''}`}></i>
                Refresh
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Company ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created At
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {isLoading ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                        <i className="ri-loader-line animate-spin mr-2"></i>
                        Loading companies...
                      </td>
                    </tr>
                  ) : companies.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                        <i className="ri-building-line text-2xl mb-2 block"></i>
                        No companies found. Create a new company ID above.
                      </td>
                    </tr>
                  ) : (
                    companies.map((company) => (
                      <tr key={company.company_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {company.company_id.toUpperCase()}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {company.description || <span className="text-gray-400 italic">No description</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {company.created_at}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <button
                            onClick={() => handleDeleteCompany(company.company_id)}
                            disabled={isDeleting === company.company_id}
                            className="text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Delete company"
                          >
                            {isDeleting === company.company_id ? (
                              <i className="ri-loader-line animate-spin"></i>
                            ) : (
                              <i className="ri-delete-bin-line"></i>
                            )}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalCompanies > 0 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  Showing {(currentPage - 1) * companiesPerPage + 1} to{' '}
                  {Math.min(currentPage * companiesPerPage, totalCompanies)} of {totalCompanies} companies
                </div>
                {totalPages > 1 ? (
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        const newPage = Math.max(1, currentPage - 1);
                        setCurrentPage(newPage);
                        loadCompanies(newPage);
                      }}
                      disabled={currentPage === 1 || isLoading}
                      className="px-3 py-1.5"
                    >
                      <i className="ri-arrow-left-line"></i>
                    </Button>
                    <div className="flex items-center space-x-1">
                      {getPaginationPages(currentPage, totalPages).map((page, idx) => {
                        if (page === 'ellipsis') {
                          return (
                            <span key={`ellipsis-${idx}`} className="px-2 py-1.5 text-sm text-gray-500">
                              ...
                            </span>
                          );
                        }
                        const pageNum = page as number;
                        return (
                          <button
                            key={pageNum}
                            onClick={() => {
                              setCurrentPage(pageNum);
                              loadCompanies(pageNum);
                            }}
                            disabled={isLoading}
                            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                              currentPage === pageNum
                                ? 'bg-indigo-600 text-white'
                                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => {
                        const newPage = Math.min(totalPages, currentPage + 1);
                        setCurrentPage(newPage);
                        loadCompanies(newPage);
                      }}
                      disabled={currentPage === totalPages || isLoading}
                      className="px-3 py-1.5"
                    >
                      <i className="ri-arrow-right-line"></i>
                    </Button>
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">Page 1 of 1</div>
                )}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

